import React, { UIEventHandler } from 'react';
import type { RefObject } from 'react';

type TUseHorizontalScroll = {
  scrollContainerRef: RefObject<HTMLDivElement>;
  isAtStart: boolean;
  isAtEnd: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
  handleScroll: UIEventHandler<HTMLDivElement>;
  scrollTo: (shift: number) => void;
  currentIndex: number;
  totalItems: number;
  scrollToIndex: (index: number) => void;
};

export const useHorizontalScroll = ({
  totalItems,
}: {
  totalItems: number;
}): TUseHorizontalScroll => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = React.useState<boolean>(true);
  const [isAtEnd, setIsAtEnd] = React.useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  React.useEffect(() => {
    if (!scrollContainerRef?.current) {
      return;
    }

    setIsAtEnd(
      scrollContainerRef.current.scrollWidth ===
        scrollContainerRef.current.offsetWidth,
    );
  }, [
    scrollContainerRef?.current?.scrollWidth,
    scrollContainerRef?.current?.offsetWidth,
  ]);

  const handleScroll: UIEventHandler<HTMLDivElement> = (e) => {
    if (!scrollContainerRef.current) {
      return;
    }

    const scrollLeft = e.currentTarget.scrollLeft;
    const itemWidth = e.currentTarget.scrollWidth / totalItems;
    const newIndex = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(newIndex);

    setIsAtStart(scrollContainerRef.current.scrollLeft === 0);
    setIsAtEnd(
      Math.floor(
        scrollContainerRef.current?.scrollWidth -
          scrollContainerRef.current?.scrollLeft,
      ) <= scrollContainerRef.current?.offsetWidth,
    );
  };

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const itemWidth = scrollContainerRef.current.scrollWidth / totalItems;
      const handleScrollBackup = scrollContainerRef.current.onscroll;
      scrollContainerRef.current.onscroll = null;
      scrollContainerRef.current.scrollTo({
        left:     index * itemWidth,
        behavior: 'smooth',
      });
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.onscroll = handleScrollBackup;
        }
        setCurrentIndex(index);
      }, 500); // Adjust the timeout duration as needed
    }
  };

  const scrollTo = React.useCallback((shift: number) => {
    scrollContainerRef?.current?.scrollTo({
      left:     scrollContainerRef?.current?.scrollLeft + shift,
      behavior: 'smooth',
    });
  }, []);

  const scroll = React.useCallback(
    (direction: -1 | 1) => {
      const offsetWidth = scrollContainerRef?.current?.offsetWidth;
      if (!offsetWidth) {
        return;
      }

      scrollTo(direction * offsetWidth * 0.9);
    },
    [scrollTo],
  );

  const scrollLeft = React.useCallback(() => {
    scroll(-1);
  }, [scroll]);

  const scrollRight = React.useCallback(() => {
    scroll(1);
  }, [scroll]);

  return {
    scrollContainerRef,
    isAtStart,
    isAtEnd,
    scrollLeft,
    scrollRight,
    handleScroll,
    scrollTo,
    currentIndex,
    totalItems,
    scrollToIndex,
  };
};
